import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/pro-solid-svg-icons";
import {
  AdminListCard,
  PageTabs,
  AdminListTable,
  ConfirmDeleteModal,
  useBatchSelection,
} from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { useCurrenciesList, useCurrencyMutations } from "../api";
import { currencyTypeLabel } from "../types";
import { buildCurrencyBatchActions, type CurrencyTab } from "./batchActions";
import type { Currency } from "../types";

const PAGE_SIZE = 25;

const List = (): JSX.Element => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<CurrencyTab>("active");
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);

  const sel = useBatchSelection([tab]);
  const { archive, restore } = useCurrencyMutations();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toast, setToast] = useState<{
    variant: "success" | "danger";
    text: string;
  } | null>(null);

  const { rows, pageMeta, isLoading, isFetching, refetch } = useCurrenciesList({
    page: { number: pageNumber, size: pageSize },
    filter: { active: tab === "active" },
  });

  const selectedCurrencies = useMemo(
    () => rows.filter((r) => sel.selectedIds.includes(r.id)),
    [rows, sel.selectedIds]
  );

  const columns: ColumnDef<Currency>[] = [
    {
      accessorKey: "name",
      header: "Currency",
      cell: ({ row }) => row.original.name,
    },
    {
      accessorKey: "currencyType",
      header: "Format",
      cell: ({ row }) => currencyTypeLabel(row.original.currencyType),
    },
    {
      accessorKey: "conversionRate",
      header: "Conversion Rate",
      cell: ({ row }) => row.original.conversionRate ?? "",
    },
    {
      accessorKey: "default",
      header: "Default?",
      cell: ({ row }) =>
        row.original.default ? <FontAwesomeIcon icon={faCheck} /> : "",
    },
  ];

  const runOnSelected = async (
    fn: (c: Currency) => Promise<unknown>,
    successText: (n: number) => string
  ) => {
    const targets = [...selectedCurrencies];
    let failures = 0;
    for (const c of targets) {
      try {
        await fn(c);
      } catch {
        failures += 1;
      }
    }
    setToast(
      failures === 0
        ? { variant: "success", text: successText(targets.length) }
        : {
            variant: "danger",
            text: `Failed on ${failures} of ${targets.length} currency(ies).`,
          }
    );
    sel.clear();
    refetch();
  };

  const handleConfirmDeleteOrRestore = async () => {
    setConfirmOpen(false);
    if (tab === "active") {
      await runOnSelected(
        (c) => archive(c.id),
        (n) => `${n} currency(ies) archived.`
      );
    } else {
      await runOnSelected(
        (c) => restore(c.id, c.name),
        (n) => `${n} currency(ies) restored.`
      );
    }
  };

  const batchActions = buildCurrencyBatchActions(tab, sel.selectedIds.length, {
    onDelete: () => setConfirmOpen(true),
    onRestore: () => setConfirmOpen(true),
  });

  if (isLoading) return <ScopeStackSpinner />;

  return (
    <>
      <AdminListCard
        title="Currencies"
        onNew={() => navigate("new")}
        newButtonLabel="+ Add A Currency"
        batchActions={batchActions}
        selectedCount={sel.selectedIds.length}
      >
        <PageTabs
          activeKey={tab}
          onSelect={(key) => {
            setTab(key as CurrencyTab);
            setPageNumber(1);
            sel.clear();
          }}
          tabs={[
            {
              key: "active",
              label: "Active",
              count: tab === "active" ? pageMeta.recordCount : undefined,
            },
            {
              key: "archived",
              label: "Archived",
              count: tab === "archived" ? pageMeta.recordCount : undefined,
            },
          ]}
        />
        <AdminListTable
          columns={columns}
          data={rows}
          isLoading={isFetching}
          selectedIds={sel.selectedIds}
          setSelectedIds={sel.setSelectedIds}
          onEdit={(id) => navigate(`${id}/edit`)}
          emptyMessage="No currencies found."
          paginationEnabled
          pageSizeEnabled
          totalPages={pageMeta.pageCount}
          totalRows={pageMeta.recordCount}
          currentPage={pageNumber}
          setCurrentPage={(p) => {
            sel.clear();
            setPageNumber(p);
          }}
          pageSize={pageSize}
          onPageSizeChange={(size) => {
            sel.clear();
            setPageSize(size);
            setPageNumber(1);
          }}
        />
      </AdminListCard>

      <ConfirmDeleteModal
        open={confirmOpen}
        title={tab === "active" ? "Archive currencies?" : "Restore currencies?"}
        message={`Are you sure you want to ${
          tab === "active" ? "archive" : "restore"
        } ${sel.selectedIds.length} currency(ies)?`}
        confirmLabel={tab === "active" ? "Archive" : "Restore"}
        onConfirm={handleConfirmDeleteOrRestore}
        onCancel={() => setConfirmOpen(false)}
      />

      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default List;
